// Stable ids for fonts. The name should match the canonical name of the font.
// Font variant should be separated from font base name with an underscore to guarantee unique names.
// These ids can be serialized to home files, so they should never be changed.
import { camelCaseToTitleCase } from "../utils/strings";

/* eslint-disable @typescript-eslint/naming-convention */
export enum FontId {
  Arial = "Arial",
  ArialNarrow = "ArialNarrow",
  ArialNarrow_Bold = "ArialNarrow_Bold",
  Circular = "Circular",
  Circular_Bold = "Circular_Bold",
  Circular_Book = "Circular_Book",
  Circular_Medium = "Circular_Medium",
  ComingSoon = "ComingSoon",
  CourierPrime = "CourierPrime",
  Helvetica = "Helvetica",
  OpenSans = "OpenSans",
  Programme = "Programme",
  PTSansNarrow = "PTSansNarrow",
  SourceSerif = "SourceSerif",
}
/* eslint-enable @typescript-eslint/naming-convention */

export type TextFont = {
  id: FontId;
  path: string;
  prettyName?: string;
};

export const TextFonts: Record<Exclude<FontId, FontId.Helvetica>, TextFont> = {
  [FontId.Arial]: { id: FontId.Arial, path: "/static/fonts/Arial/ARIAL.TTF" },
  [FontId.ArialNarrow]: { id: FontId.ArialNarrow, path: "/static/fonts/Arial/ARIALN.TTF" },
  [FontId.ArialNarrow_Bold]: { id: FontId.ArialNarrow_Bold, path: "/static/fonts/Arial/ARIALNB.TTF" },
  [FontId.Circular]: { id: FontId.Circular, path: "/static/fonts/Circular/Circular.otf" },
  [FontId.Circular_Bold]: {
    id: FontId.Circular_Bold,
    path: "/static/fonts/Circular/CircularBold.otf",
  },
  [FontId.Circular_Book]: {
    id: FontId.Circular_Book,
    path: "/static/fonts/Circular/CircularBook.otf",
  },
  [FontId.Circular_Medium]: {
    id: FontId.Circular_Medium,
    path: "/static/fonts/Circular/CircularMedium.otf",
  },
  [FontId.ComingSoon]: {
    id: FontId.ComingSoon,
    path: "/static/fonts/Coming_Soon/ComingSoon-Regular.ttf",
    prettyName: "Architectural",
  },
  [FontId.CourierPrime]: {
    id: FontId.CourierPrime,
    path: "/static/fonts/Courier_Prime/CourierPrime-Regular.ttf",
    prettyName: "Monospace",
  },
  [FontId.OpenSans]: {
    id: FontId.OpenSans,
    path: "/static/fonts/Open_Sans/OpenSans-Regular.ttf",
    prettyName: "Sans Serif",
  },
  [FontId.Programme]: {
    id: FontId.Programme,
    path: "/static/fonts/Programme/Programme-Regular.otf",
  },
  [FontId.PTSansNarrow]: {
    id: FontId.PTSansNarrow,
    path: "/static/fonts/PT_Sans_Narrow/PTSansNarrow-Regular.ttf",
    prettyName: "Sans Serif Narrow",
  },
  // Note that this Source Serif font has been downloaded from:
  // https://github.com/adobe-fonts/source-serif/releases/tag/4.005R
  // not Google Fonts. the Google Fonts version showed some artifacts when rendered using troika-three-text.
  [FontId.SourceSerif]: {
    id: FontId.SourceSerif,
    path: "/static/fonts/Source_Serif_4/SourceSerif4-Regular.ttf.woff",
    prettyName: "Serif",
  },
} as const;

export function getFontFromId(fontId: FontId): TextFont {
  if (fontId === FontId.Helvetica) {
    // Can't support Helvetica as a TextFont.
    return TextFonts.ArialNarrow;
  }
  return TextFonts[fontId];
}

export function fontPrettyName(fontId: FontId): string {
  const font = getFontFromId(fontId);
  if (font.prettyName) {
    return font.prettyName;
  }
  const prettyName = camelCaseToTitleCase(font.id.replace("_", " "));
  font.prettyName = prettyName;
  return prettyName;
}
